import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { parseJwt } from '../../auth/utils';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { RETRYING_CONNECTION_INFO_TOAST_KEY, TERMINAL_CONNECTION_ERROR_TOAST_KEY } from '../../provider/sync';
import { useAppDependenciesContext } from '../../state';
import { toast } from '../../toasts';
import {
  reverseProxyWebsockets,
  twilioIdentityAtom,
  usedTwilioWebsocketsSuccessfullyAtom,
  usingWebsocketsReverseProxyAtom,
} from '../../video-sdk/twilio';
import {
  syncClientConnectionErrorAtom,
  syncClientConnectionStateAtom,
  twilioSyncClientAtom,
  twilioSyncConnectionAttemptCountAtom,
} from '../state';
import { ConnectionError, ConnectionState, ConnectionStateStatus } from '../types';
import { getMaxTwilioWebsocketConnectionRetriesBeforeFallback } from './utils';

export function useTeardownTwilioSyncClientCallback() {
  const setTwilioSyncClient = useSetAtom(twilioSyncClientAtom);
  const getTwilioSyncClient = useAtomCallbackMemoized((get) => get(twilioSyncClientAtom), []);

  return useCallback(
    function teardownTwilioSyncClient() {
      const client = getTwilioSyncClient();
      if (!client) return;
      client.removeAllListeners();
      client.shutdown();
      setTwilioSyncClient(undefined);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

type InitTwilioSyncClientOpts = {
  jwt?: string;
  onTokenExpired?: () => void;
  onTokenAboutToExpire?: () => void;
  onConnectionError?: (error: ConnectionError) => void;
  onConnectionStateChanged?: (newState: ConnectionStateStatus) => void;
};

export function useInitTwilioSyncClientCallback() {
  const setTwilioSyncClient = useSetAtom(twilioSyncClientAtom);
  const setSyncClientState = useSetAtom(syncClientConnectionStateAtom);
  const setTwilioIdentity = useSetAtom(twilioIdentityAtom);
  const teardownTwilioSyncClient = useTeardownTwilioSyncClientCallback();
  const setUsingWebsocketsReverseProxy = useSetAtom(usingWebsocketsReverseProxyAtom);
  const setSyncClientConnectionError = useSetAtom(syncClientConnectionErrorAtom);
  const setUsedTwilioWsSuccessfully = useSetAtom(usedTwilioWebsocketsSuccessfullyAtom);
  const getUsingWebsocketsReverseProxy = useAtomCallbackMemoized((get) => get(usingWebsocketsReverseProxyAtom), []);
  const getUsedTwilioWsSuccessfully = useAtomCallbackMemoized((get) => get(usedTwilioWebsocketsSuccessfullyAtom), []);
  const setTwilioSyncConnectionAttemptCount = useSetAtom(twilioSyncConnectionAttemptCountAtom);
  const getTwilioSyncConnectionAttemptCount = useAtomCallbackMemoized(
    (get) => get(twilioSyncConnectionAttemptCountAtom),
    [],
  );
  const { logger, Statsig, SyncClient } = useAppDependenciesContext();

  return useCallback(
    function initTwilioSyncClient({
      jwt,
      onTokenExpired,
      onTokenAboutToExpire,
      onConnectionError,
      onConnectionStateChanged,
    }: InitTwilioSyncClientOpts = {}) {
      if (!jwt) return;

      teardownTwilioSyncClient();

      if (getUsingWebsocketsReverseProxy()) {
        reverseProxyWebsockets();
      }

      const client = new SyncClient(jwt);
      logger.info('Initializing twilio sync client');

      client.on('tokenExpired', () => {
        logger.warn('Twilio sync token expired');
        onTokenExpired?.();
      });

      client.on('tokenAboutToExpire', () => {
        logger.warn('Twilio sync token about to expire');
        onTokenAboutToExpire?.();
      });

      client.on('connectionError', (connectionError) => {
        setTwilioSyncConnectionAttemptCount((prev) => prev + 1);

        const connectionAttemptCount = getTwilioSyncConnectionAttemptCount();
        const belowConnectionRetryLimit =
          connectionAttemptCount <= getMaxTwilioWebsocketConnectionRetriesBeforeFallback({ Statsig });

        const shouldUseTwilioWsFallback =
          !getUsedTwilioWsSuccessfully() && !getUsingWebsocketsReverseProxy() && !belowConnectionRetryLimit;

        if (shouldUseTwilioWsFallback) {
          setUsingWebsocketsReverseProxy(true);
        } else {
          onConnectionError?.(connectionError);
          setSyncClientState('error');
          setSyncClientConnectionError(connectionError);
        }

        logger.error(`Twilio sync connection error: ${connectionError.message}`, {
          connectionError,
          connectionAttemptCount,
        });
      });

      client.on('connectionStateChanged', (newState) => {
        logger.debug('Twilio sync connection state change', {
          twilioSyncState: newState,
        });
        if (newState === ConnectionState.CONNECTED) {
          toast.close(RETRYING_CONNECTION_INFO_TOAST_KEY);
          toast.close(TERMINAL_CONNECTION_ERROR_TOAST_KEY);
          setSyncClientConnectionError(undefined);
          setUsedTwilioWsSuccessfully(true);
          setTwilioSyncConnectionAttemptCount(0);
        }
        onConnectionStateChanged?.(newState);
        setSyncClientState(newState);
      });

      const identity = parseJwt<{ grants: { identity: string } }>(jwt)?.grants?.identity;
      setTwilioSyncClient(client);
      setTwilioIdentity(identity);
      return client;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

/**
 * This hook is responsible for the initialization and tear down of the Twilio
 * Sync client in Jotai state. Recommend no more than one usage per experience
 * (provider/visitor).
 */
export function useInitTwilioSyncClient(opts: InitTwilioSyncClientOpts) {
  const initTwilioSyncClient = useInitTwilioSyncClientCallback();
  const teardownTwilioSyncClient = useTeardownTwilioSyncClientCallback();
  const { jwt } = opts;
  const usingWebsocketsReverseProxy = useAtomValue(usingWebsocketsReverseProxyAtom);

  useEffect(
    function handleTwilioSyncClient() {
      if (!jwt) return;
      initTwilioSyncClient(opts);
      return teardownTwilioSyncClient;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jwt, usingWebsocketsReverseProxy],
  );
}
